import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useRef,
} from "react";
import { Outlet } from "react-router-dom";
import { isAxiosError } from "axios";
import { profileClient } from "@/shared/api/clients";
import type { StationFormData } from "./StationForm";
import type { Vendor } from "../ConnectBodycam/vendorConfig";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Station {
  id: string;
  name: string;
  station_status: string;
  subscription_tier?: string;
  seat_limit?: number;
  station_type?: string | null;
}

export interface StationDetail {
  id: string;
  name: string;
  external_id?: string;
  metadata: Record<string, string | null | undefined>;
}

interface KardaAdminState {
  stations: Station[] | null; // null = not yet fetched
  isLoadingStations: boolean;

  stationDetails: Record<string, StationDetail>;
  loadingStationDetails: Record<string, boolean>;

  // Key present (even if value is null) means fetched; key absent means not fetched
  bodycamIntegrations: Record<string, Vendor | null>;
  loadingBodycam: Record<string, boolean>;
}

export interface KardaAdminContextValue extends KardaAdminState {
  fetchStations: () => Promise<void>;
  createStation: (data: StationFormData) => Promise<void>;
  fetchStation: (stationId: string) => Promise<void>;
  updateStation: (stationId: string, data: StationFormData) => Promise<void>;
  fetchBodycamIntegration: (stationId: string) => Promise<void>;
  setBodycamIntegration: (stationId: string, vendor: Vendor | null) => void;
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

type Action =
  | { type: "SET_LOADING_STATIONS"; loading: boolean }
  | { type: "SET_STATIONS"; stations: Station[] }
  | { type: "SET_LOADING_STATION_DETAIL"; stationId: string; loading: boolean }
  | { type: "SET_STATION_DETAIL"; stationId: string; detail: StationDetail }
  | { type: "SET_LOADING_BODYCAM"; stationId: string; loading: boolean }
  | {
      type: "SET_BODYCAM_INTEGRATION";
      stationId: string;
      vendor: Vendor | null;
    };

const initialState: KardaAdminState = {
  stations: null,
  isLoadingStations: false,
  stationDetails: {},
  loadingStationDetails: {},
  bodycamIntegrations: {},
  loadingBodycam: {},
};

function reducer(state: KardaAdminState, action: Action): KardaAdminState {
  switch (action.type) {
    case "SET_LOADING_STATIONS":
      return { ...state, isLoadingStations: action.loading };
    case "SET_STATIONS":
      return {
        ...state,
        stations: action.stations,
        isLoadingStations: false,
      };
    case "SET_LOADING_STATION_DETAIL":
      return {
        ...state,
        loadingStationDetails: {
          ...state.loadingStationDetails,
          [action.stationId]: action.loading,
        },
      };
    case "SET_STATION_DETAIL":
      return {
        ...state,
        stationDetails: {
          ...state.stationDetails,
          [action.stationId]: action.detail,
        },
        loadingStationDetails: {
          ...state.loadingStationDetails,
          [action.stationId]: false,
        },
      };
    case "SET_LOADING_BODYCAM":
      return {
        ...state,
        loadingBodycam: {
          ...state.loadingBodycam,
          [action.stationId]: action.loading,
        },
      };
    case "SET_BODYCAM_INTEGRATION":
      return {
        ...state,
        bodycamIntegrations: {
          ...state.bodycamIntegrations,
          [action.stationId]: action.vendor,
        },
        loadingBodycam: {
          ...state.loadingBodycam,
          [action.stationId]: false,
        },
      };
    default:
      return state;
  }
}

// ─── Payload builders ─────────────────────────────────────────────────────────

function buildPayload(data: StationFormData, mode: "create" | "update") {
  const sapc = data.service_area_plus_code?.trim() || null;
  const contactNumber = data.contact_number.trim() || null;
  return {
    name: data.name,
    state: data.state.toUpperCase(),
    metadata: {
      city: data.city,
      state: data.state.toUpperCase(),
      zip_code: data.zip_code,
      county: data.county,
      address: data.address,
      contact_email: data.contact_email,
      ...(mode === "create" && !contactNumber
        ? {}
        : { contact_number: contactNumber }),
      rms_used: data.rms_used || null,
      bodycam_vendor: data.bodycam_vendor,
      seat_limit: parseInt(data.seat_limit, 10),
      subscription_tier: data.subscription_tier,
      service_area_plus_code: sapc,
    },
  };
}

// ─── Context ──────────────────────────────────────────────────────────────────

const KardaAdminContext = createContext<KardaAdminContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const KardaAdminProvider: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Use a ref so callbacks always read the latest state without stale closures.
  const stateRef = useRef(state);
  stateRef.current = state;

  // ── Stations list ────────────────────────────────────────────────────────

  const fetchStations = useCallback(async () => {
    const s = stateRef.current;
    // Skip if already loaded for this includeInactive value, or a request is in flight.
    if (s.stations !== null || s.isLoadingStations) return;
    dispatch({ type: "SET_LOADING_STATIONS", loading: true });
    try {
      const resp = await profileClient.get<{ stations: Station[] }>(
        `/v1/stations?includeInactive=${true}`,
      );
      dispatch({ type: "SET_STATIONS", stations: resp.data.stations });
    } catch (err) {
      dispatch({ type: "SET_LOADING_STATIONS", loading: false });
      throw err;
    }
  }, []);

  const createStation = useCallback(async (data: StationFormData) => {
    try {
      await profileClient.post("/v1/stations", buildPayload(data, "create"));
      // Bust cache and re-fetch with current includeInactive value.
      dispatch({ type: "SET_LOADING_STATIONS", loading: true });
      const resp = await profileClient.get<{ stations: Station[] }>(
        `/v1/stations?includeInactive=${true}`,
      );
      dispatch({ type: "SET_STATIONS", stations: resp.data.stations });
    } catch (err) {
      dispatch({ type: "SET_LOADING_STATIONS", loading: false });
      throw err;
    }
  }, []);

  // ── Station detail ───────────────────────────────────────────────────────

  const fetchStation = useCallback(async (stationId: string) => {
    const s = stateRef.current;
    // Skip if already cached or request in flight.
    if (s.stationDetails[stationId] || s.loadingStationDetails[stationId])
      return;
    dispatch({ type: "SET_LOADING_STATION_DETAIL", stationId, loading: true });
    try {
      const resp = await profileClient.get<{ station: StationDetail }>(
        `/v1/stations/${stationId}`,
      );
      dispatch({
        type: "SET_STATION_DETAIL",
        stationId,
        detail: resp.data.station,
      });
    } catch (err) {
      dispatch({
        type: "SET_LOADING_STATION_DETAIL",
        stationId,
        loading: false,
      });
      throw err;
    }
  }, []);

  const updateStation = useCallback(
    async (stationId: string, data: StationFormData) => {
      try {
        await profileClient.patch(
          `/v1/stations/${stationId}`,
          buildPayload(data, "update"),
        );
      } catch (err) {
        throw err; // exception handled by caller
      }
      try {
        // Refresh the station detail cache.
        const resp = await profileClient.get<{ station: StationDetail }>(
          `/v1/stations/${stationId}`,
        );
        dispatch({
          type: "SET_STATION_DETAIL",
          stationId,
          detail: resp.data.station,
        });
        // Refresh the stations list so renames/metadata changes are reflected on the left-hand list.
        const stationsResp = await profileClient.get<{ stations: Station[] }>(
          `/v1/stations?includeInactive=${true}`,
        );
        dispatch({
          type: "SET_STATIONS",
          stations: stationsResp.data.stations,
        });
      } catch (err) {
        // non issue if exception is raised
      }
    },
    [],
  );

  // ── Bodycam integration ──────────────────────────────────────────────────

  const fetchBodycamIntegration = useCallback(async (stationId: string) => {
    const s = stateRef.current;
    // Key presence (not value) determines if we've fetched before.
    if (stationId in s.bodycamIntegrations || s.loadingBodycam[stationId])
      return;
    dispatch({ type: "SET_LOADING_BODYCAM", stationId, loading: true });
    try {
      const resp = await profileClient.get(
        `/v1/stations/${stationId}/integrations/bodycam/resolve`,
      );
      const vendorName = resp.data.vendorName?.toUpperCase() as
        | Vendor
        | undefined;
      dispatch({
        type: "SET_BODYCAM_INTEGRATION",
        stationId,
        vendor: vendorName ?? null,
      });
    } catch (err) {
      // 404 means no integration configured — cache as null.
      if (isAxiosError(err) && err.response?.status === 404) {
        dispatch({ type: "SET_BODYCAM_INTEGRATION", stationId, vendor: null });
      } else {
        dispatch({ type: "SET_LOADING_BODYCAM", stationId, loading: false });
        throw err;
      }
    }
  }, []);

  const setBodycamIntegration = useCallback(
    (stationId: string, vendor: Vendor | null) => {
      dispatch({ type: "SET_BODYCAM_INTEGRATION", stationId, vendor });
    },
    [],
  );

  // ── Context value ────────────────────────────────────────────────────────

  const value: KardaAdminContextValue = {
    ...state,
    fetchStations,
    createStation,
    fetchStation,
    updateStation,
    fetchBodycamIntegration,
    setBodycamIntegration,
  };

  return (
    <KardaAdminContext.Provider value={value}>
      <Outlet />
    </KardaAdminContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface BodycamIntegrationState {
  /** The connected vendor, `null` if no integration, `undefined` if not yet fetched. */
  vendor: Vendor | null | undefined;
  isLoading: boolean;
  isFetched: boolean;
}

export function useKardaAdmin() {
  const ctx = useContext(KardaAdminContext);
  if (!ctx)
    throw new Error("useKardaAdmin must be used inside KardaAdminProvider");

  function getBodycamIntegration(stationId: string): BodycamIntegrationState {
    if (!ctx) {
      return {
        vendor: undefined,
        isLoading: false,
        isFetched: false,
      };
    }
    const isFetched = stationId in ctx.bodycamIntegrations;
    return {
      vendor: isFetched ? ctx.bodycamIntegrations[stationId] : undefined,
      isLoading: ctx.loadingBodycam[stationId] ?? false,
      isFetched,
    };
  }

  return { ...ctx, getBodycamIntegration };
}
